import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTableColumns, faLayerGroup, faAddressCard } from "@fortawesome/free-solid-svg-icons";
import { Modal } from "react-bootstrap";
import Content from "../../../../layout/Content";
import ContentHeader from "../../../../layout/ContenteHeader";
import CompleteDropdown from "../../../../layout/Dropdowns/CompleteDropdown";
import { btn, btnHoverStyle } from "../../../../layout/Buttons/buttonStyles";
import pipelineApi from "@serie3/freelancesplaceapi/api/crm/pipeline";
import crudApi from "@serie3/common/api/crudApi";
import PipelineConfig from "@serie3/freelancesplaceapi/Config/Crm/PipelineConfig";

const ProjectRow = ({ project, onOpen, onDelete }) => {
  const [hovered, setHovered] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const accent = project.color || "#1a73e8";

  useEffect(() => {
    if (project.file_name) {
      crudApi.getBinary(PipelineConfig.project.filePreview + project.id).then((res) => {
        if (res?.data) setLogoUrl(URL.createObjectURL(res.data));
      });
    }
  }, [project.id]);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onOpen}
      style={{
        background: "#fff",
        borderRadius: "12px",
        boxShadow: hovered ? "0 4px 20px rgba(0,0,0,0.1)" : "0 1px 6px rgba(0,0,0,0.07)",
        borderLeft: `4px solid ${accent}`,
        padding: "16px 20px",
        display: "flex",
        alignItems: "center",
        gap: "16px",
        transition: "box-shadow 0.15s",
        cursor: "pointer",
      }}
    >
      <div style={{
        width: "40px", height: "40px", borderRadius: "10px", flexShrink: 0,
        background: logoUrl ? "#f5f5f5" : `linear-gradient(135deg, ${accent} 0%, ${accent}cc 100%)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: `0 2px 8px ${accent}44`, overflow: "hidden",
      }}>
        {logoUrl
          ? <img src={logoUrl} alt="logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          : <span style={{ color: "#fff", fontSize: "1.1rem", fontWeight: 800 }}>{project.name?.charAt(0)?.toUpperCase() || "?"}</span>
        }
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: "0.97rem", color: "#212121", marginBottom: "3px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {project.name}
        </div>
        {project.description && (
          <div style={{ fontSize: "0.8rem", color: "#9e9e9e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {project.description}
          </div>
        )}
      </div>

      {project.steps !== undefined && (
        <div style={{ display: "flex", gap: "14px", flexShrink: 0 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "0.78rem", color: "#9e9e9e", fontWeight: 500 }}>
            <FontAwesomeIcon icon={faLayerGroup} style={{ fontSize: "0.68rem" }} />
            {Array.isArray(project.steps) ? project.steps.length : (project.steps ?? 0)} columns
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "0.78rem", color: "#9e9e9e", fontWeight: 500 }}>
            <FontAwesomeIcon icon={faAddressCard} style={{ fontSize: "0.68rem" }} />
            {Array.isArray(project.entries) ? project.entries.length : (project.entries ?? 0)} cards
          </span>
        </div>
      )}

      <div style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
        <CompleteDropdown editAction={onOpen} deleteAction={onDelete} />
      </div>
    </div>
  );
};

const PipelineIndex = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    pipelineApi.getProjects().then((list) => {
      setProjects(list);
      setLoading(false);
    });
  }, []);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    const res = await pipelineApi.createProject({ name: newName.trim(), description: "" });
    setCreating(false);
    setShowCreate(false);
    setNewName("");
    if (res?.status === 200 || res?.status === 201) {
      navigate(`/crm/pipeline/${res.data.id}`);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await pipelineApi.deleteProject(deleteTarget.id);
    setProjects((prev) => prev.filter((p) => p.id !== deleteTarget.id));
    setDeleteTarget(null);
  };

  const NewButton = () => (
    <>
      <style>{btnHoverStyle}</style>
      <button className="btn-modern" style={{ ...btn.base, ...btn.primary }} onClick={() => setShowCreate(true)}>
        <FontAwesomeIcon icon={faPlus} /> New Pipeline
      </button>
    </>
  );

  const Title = () => <b>Pipelines</b>;

  return (
    <>
      <style>{`.modal-pipeline-content { background: transparent !important; border: none !important; box-shadow: none !important; }`}</style>
      {/* Create modal */}
      <Modal show={showCreate} onHide={() => { setShowCreate(false); setNewName(""); }} onShow={() => setTimeout(() => inputRef.current?.focus(), 50)} centered contentClassName="modal-pipeline-content">
        <div style={{ background: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)", padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", borderRadius: "14px 14px 0 0" }}>
          <span style={{ fontWeight: 700, fontSize: "1rem", color: "#fff" }}>New Pipeline</span>
          <button onClick={() => { setShowCreate(false); setNewName(""); }} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "8px", width: "30px", height: "30px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: "1rem" }}>✕</button>
        </div>
        <div style={{ padding: "24px", background: "#f8f9fb" }}>
          <label style={{ display: "block", fontWeight: 600, fontSize: "0.88rem", color: "#424242", marginBottom: "8px" }}>Pipeline name</label>
          <input
            ref={inputRef}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); if (e.key === "Escape") { setShowCreate(false); setNewName(""); } }}
            placeholder="Enter pipeline name…"
            style={{ width: "100%", padding: "10px 14px", border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "0.92rem", outline: "none" }}
          />
        </div>
        <div style={{ background: "#fff", borderTop: "1px solid #f0f0f0", padding: "14px 24px", display: "flex", justifyContent: "flex-end", gap: "10px", borderRadius: "0 0 14px 14px" }}>
          <button onClick={() => { setShowCreate(false); setNewName(""); }} style={{ padding: "8px 18px", background: "#f5f5f5", color: "#424242", border: "none", borderRadius: "8px", fontWeight: 600, fontSize: "0.88rem", cursor: "pointer" }}>Cancel</button>
          <button onClick={handleCreate} disabled={!newName.trim() || creating} style={{ padding: "8px 20px", background: "#1a73e8", color: "#fff", border: "none", borderRadius: "8px", fontWeight: 700, fontSize: "0.88rem", cursor: "pointer", opacity: !newName.trim() || creating ? 0.6 : 1 }}>
            {creating ? "Creating…" : "Create"}
          </button>
        </div>
      </Modal>

      {/* Delete confirm modal */}
      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered size="sm" contentClassName="modal-pipeline-content">
        <div style={{ background: "linear-gradient(135deg, #c62828 0%, #b71c1c 100%)", padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", borderRadius: "14px 14px 0 0" }}>
          <span style={{ fontWeight: 700, fontSize: "1rem", color: "#fff" }}>Delete Pipeline</span>
          <button onClick={() => setDeleteTarget(null)} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "8px", width: "30px", height: "30px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: "1rem" }}>✕</button>
        </div>
        <div style={{ padding: "24px", background: "#f8f9fb", fontSize: "0.92rem", color: "#424242" }}>
          Delete <strong>"{deleteTarget?.name}"</strong> and all its columns and cards?
        </div>
        <div style={{ background: "#fff", borderTop: "1px solid #f0f0f0", padding: "14px 24px", display: "flex", justifyContent: "flex-end", gap: "10px", borderRadius: "0 0 14px 14px" }}>
          <button onClick={() => setDeleteTarget(null)} style={{ padding: "8px 18px", background: "#f5f5f5", color: "#424242", border: "none", borderRadius: "8px", fontWeight: 600, fontSize: "0.88rem", cursor: "pointer" }}>Cancel</button>
          <button onClick={handleDelete} style={{ padding: "8px 20px", background: "#c62828", color: "#fff", border: "none", borderRadius: "8px", fontWeight: 700, fontSize: "0.88rem", cursor: "pointer" }}>Delete</button>
        </div>
      </Modal>

      <ContentHeader>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <div style={{
            width: "46px", height: "46px", borderRadius: "12px",
            background: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)",
            color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "1.2rem", flexShrink: 0, boxShadow: "0 4px 12px rgba(26,115,232,0.3)",
          }}>
            <FontAwesomeIcon icon={faTableColumns} />
          </div>
          <div>
            <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#212121", lineHeight: 1.2 }}>Pipelines</div>
            <div style={{ fontSize: "0.85rem", color: "#9e9e9e", marginTop: "2px" }}>Manage your sales pipelines and contact boards</div>
          </div>
        </div>
      </ContentHeader>

      <Content Title={Title} AdditionalButtons={NewButton}>
        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "60px", gap: "12px", color: "#9e9e9e" }}>
            <div style={{ width: "28px", height: "28px", borderRadius: "50%", border: "3px solid #e8f0fe", borderTopColor: "#1a73e8", animation: "spin 0.8s linear infinite" }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : projects.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#9e9e9e", fontSize: "0.95rem" }}>
            No pipelines yet. Create your first one!
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "4px 0" }}>
            {projects.map((project, index) => (
              <ProjectRow
                key={project.id ?? index}
                project={project}
                onOpen={() => navigate(`/crm/pipeline/${project.id}`)}
                onDelete={() => setDeleteTarget(project)}
              />
            ))}
          </div>
        )}
      </Content>
    </>
  );
};

export default PipelineIndex;
